import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { Sparkles, Send, Loader2, Quote, User } from "lucide-react";

const SUGGESTIONS = [
  "Where was my Johnson family living between 1870 and 1900?",
  "Which documents connect Mary Johnson to Sarah Johnson?",
  "What evidence links the Johnsons to the Choctaw?",
];

export default function AskRecords() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef();

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput(""); setLoading(true);
    try {
      const r = await api.post("/ask-records", { question });
      setMessages((m) => [...m, { role: "ai", ...r.data }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", answer: "Something went wrong reaching the archive.", citations: [], evidence_status: "UNVERIFIED" }]);
    } finally {
      setLoading(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  return (
    <div data-testid="module-ask-records">
      <PageHeader
        eyebrow="Ask My Records • Claude Sonnet 4.6"
        title="Ask My Records"
        subtitle="A private research assistant that answers only from your own uploaded documents — with clickable citations. It will not invent facts about your family."
      />

      <Panel className="flex flex-col h-[62vh]">
        <div className="flex-1 overflow-y-auto hp-scroll p-5 space-y-5" data-testid="chat-window">
          {messages.length === 0 && (
            <div className="h-full grid place-items-center text-center">
              <div>
                <Sparkles size={30} className="text-[#D4AF37] mx-auto mb-4" />
                <p className="text-[#756D7E] mb-5 text-sm">Ask a question about your vault. Try one of these:</p>
                <div className="flex flex-col gap-2 max-w-md mx-auto">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => ask(s)}
                      className="text-left text-sm rounded-xl border border-[#E4DCD3] bg-white px-4 py-2.5 hover:border-[#D4AF37] transition-colors text-[#3F1E68]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#2A1442] text-white px-4 py-2.5 text-sm flex items-start gap-2">
                  <span>{m.text}</span><User size={15} className="shrink-0 mt-0.5 text-[#E7C965]" />
                </div>
              </div>
            ) : (
              <div key={i} className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white border border-[#E4DCD3] px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    {m.evidence_status && <EvidenceBadge status={m.evidence_status} />}
                  </div>
                  <p className="text-sm text-[#1A171D] leading-relaxed whitespace-pre-wrap">{m.answer}</p>
                  {m.caveat && <p className="mt-2 text-xs text-[#B45309] bg-[#FFFBEB] rounded-lg p-2">{m.caveat}</p>}
                  {m.citations?.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <div className="text-[10px] uppercase font-mono tracking-wider text-[#8F7220]">Sources</div>
                      {m.citations.map((c, j) => (
                        <div key={j} className="rounded-lg bg-[#F3EEFA] border border-[#E4DCD3] p-2.5" data-testid="chat-citation">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#59288E]">
                            <Quote size={12} /> [DOC {c.doc}] {c.title}
                          </div>
                          {c.quote && <p className="mt-1 text-xs text-[#756D7E] italic">"{c.quote}"</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="rounded-2xl bg-white border border-[#E4DCD3] px-4 py-3 flex items-center gap-2 text-[#756D7E] text-sm">
                <Loader2 size={15} className="animate-spin text-[#59288E]" /> Searching your records…
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-[#E4DCD3] p-4">
          <div className="flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask about your ancestors, records or citations…"
              className="flex-1 rounded-full bg-[#F4EFE6] border border-[#E4DCD3] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
            />
            <button
              data-testid="chat-send-btn"
              onClick={() => ask()}
              disabled={loading}
              className="h-11 w-11 grid place-items-center rounded-full bg-[#D4AF37] text-[#180D24] hover:bg-[#C59B27] transition-colors disabled:opacity-60"
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
